import {
	call,
	put,
	spawn,
	takeLatest,
	all,
	fork,
	join,
	select,
	take,
	delay,
} from "redux-saga/effects";
import { channel, buffers, END } from "redux-saga";
import type { Channel, Task } from "redux-saga";
import {
	documentUploadStart,
	documentUploadUpdate,
	documentUploadProgress,
	documentUploadError,
	initiateUpload,
	s3BucketInfoSuccess,
	s3BucketInfoFailed,
	s3BucketInfoRequested,
	setS3FolderName,
} from "../slices/upload.slice";
const httpUploadProgress = "httpUploadProgress";

import {
	FileUploadStatus,
	DriveFileUpload,
	IS3Config,
} from "../types/upload.types";
import { S3Upload } from "../../services/s3upload.services";
import { getS3BucketDetails } from "../../services/api.services";
import { logClientError } from "../../services/client-logger.service";
import { RootState, store } from "../root";
import {
	decryptAES,
	getFileUploadProgressReturnValue,
} from "../../utils/common.utils";

import { fileStore } from "../../hooks/useUpload";
import { AxiosResponse } from "axios";
import { orderItemUpdateRequested } from "../slices/order.slice";
import { CompleteMultipartUploadCommandOutput } from "@aws-sdk/client-s3";
import { SagaIterator } from "redux-saga";
import { toast } from "sonner";

// const BATCH_SIZE = 10;

const HTTP_SUCCESS_CODE = 200;

const DECRYPTION_KEY = "LOG";

/* ============================================================
   STEP 1: Fetch S3 Bucket Configuration
   ============================================================ */
function* fetchS3BucketDetails() {
	try {
		const response: AxiosResponse = yield call(getS3BucketDetails);

		if (!response?.data?.data?.bucketdescription) {
			throw new Error(
				"Invalid S3 bucket configuration response: missing bucketdescription"
			);
		}

		const encryptedBucketData = response.data.data.bucketdescription;

		try {
			// Verify we can decrypt the bucket config
			const decrypted = decryptAES(encryptedBucketData, DECRYPTION_KEY);
			const parsed = JSON.parse(decrypted);

			if (
				!parsed?.BucketName ||
				!parsed?.CognitoIdentityPoolID ||
				!parsed?.AWSRegionEndpoint
			) {
				throw new Error(
					"Decrypted S3 config missing required fields: BucketName, CognitoIdentityPoolID, or AWSRegionEndpoint"
				);
			}

			console.log(" S3 bucket configuration loaded successfully");
		} catch (decryptError: any) {
			throw new Error(
				`Failed to decrypt/parse S3 bucket config: ${decryptError?.message}`
			);
		}

		yield put(s3BucketInfoSuccess(encryptedBucketData));
		yield put(initiateUpload());
	} catch (error: any) {
		const errorDetails = {
			stage: "fetchS3BucketDetails",
			error: error?.message,
			statusCode: error?.response?.status,
			endpoint: "GET_S3_BUCKET_DETAILS",
			timestamp: new Date().toISOString(),
		};
		console.error(
			" Failed to fetch S3 bucket details:",
			JSON.stringify(errorDetails, null, 2)
		);
		yield call(logClientError, {
			level: "error",
			event: "s3_bucket_details_failed",
			message: "Failed to fetch S3 bucket details",
			error,
			context: errorDetails,
		});
		yield put(s3BucketInfoFailed(error?.message ?? "Unknown error"));
	}
}

// For internet connectivity issue
const waitForInternet = () =>
	new Promise((resolve) => {
		const stabilizeAndResolve = () => {
			console.log(
				"🔌 Network connection detected. Waiting 5 seconds for OS to stabilize internet access..."
			);
			setTimeout(() => resolve(true), 5000); // 5-second mandatory buffer
		};

		if (navigator.onLine) {
			stabilizeAndResolve();
		} else {
			window.addEventListener("online", stabilizeAndResolve, { once: true });
		}
	});

/* ============================================================
   STEP 2: Handle Single File Upload
   ============================================================ */
function* handleSingleUpload(fileMeta: DriveFileUpload, s3Config: IS3Config) {
	const { orderId } = yield select((state: RootState) => state.order);

	const {
		upload: { files },
	} = yield select((state: RootState) => state);
	try {
		if (fileMeta.status === FileUploadStatus.UnsupportedFileType) {
			yield put(orderItemUpdateRequested({ file: fileMeta }));
			return;
		}
		yield put(documentUploadStart(fileMeta.id));

		const realFile: File | null = fileStore.getFileById(fileMeta.id);

		if (!realFile) {
			console.error("File missing in local map:", fileMeta.id);
			yield call(logClientError, {
				level: "error",
				event: "upload_file_missing_local_map",
				message: "File is missing from client fileStore before upload",
				context: {
					fileId: fileMeta.id,
					fileName: fileMeta.name,
					fileSize: fileMeta.size,
				},
			});

			yield put(
				documentUploadUpdate({
					id: fileMeta.id,
					changes: { status: FileUploadStatus.Failed },
				})
			);
			return;
		}

		const fileKey = `${s3Config.folderName}/${orderId}/${fileMeta.id}.${fileMeta.name?.split(".").reverse()[0]}`;

		const uploadPayload = {
			...fileMeta,
			file: realFile,
			key: fileKey,
		};
		yield put(documentUploadProgress({ id: fileMeta.id, progress: 1 }));

		const uploader = new S3Upload(s3Config, uploadPayload);

		const progressHandler = (progress: any) => {
			if (progress.loaded && progress.total) {
				const fileProgress = Math.floor(
					(progress.loaded / progress.total) * 99
				);
				const oldProgress: number =
					files.find((f: any) => f.id === fileMeta.id)?.progress || 0;
				if (
					oldProgress === 0 ||
					(fileProgress > 0 &&
						fileProgress - oldProgress >=
							getFileUploadProgressReturnValue(progress?.total))
				) {
					store.dispatch(
						documentUploadProgress({ id: fileMeta.id, progress: fileProgress })
					);
				}
			}
		};

		uploader.parallelUploads3.on(httpUploadProgress, progressHandler);

		// Wait for S3 to finish
		let uploadCompleteOutput: CompleteMultipartUploadCommandOutput;
		try {
			uploadCompleteOutput = yield uploader.parallelUploads3.done();
		} catch (uploadError: any) {
			uploader.parallelUploads3.off(httpUploadProgress, progressHandler);
			const errorDetails = {
				fileId: fileMeta.id,
				fileName: fileMeta.name,
				stage: "multipart_upload_completion",
				error: uploadError?.message,
				code: uploadError?.code,
				statusCode: uploadError?.$metadata?.httpStatusCode,
			};
			console.error(
				"Multipart upload failed:",
				JSON.stringify(errorDetails, null, 2)
			);
			throw uploadError;
		}

		uploader.parallelUploads3.off(httpUploadProgress, progressHandler);

		// Validate HTTP status code
		if (uploadCompleteOutput?.$metadata?.httpStatusCode !== HTTP_SUCCESS_CODE) {
			throw new Error(
				`S3 upload failed: HTTP ${uploadCompleteOutput?.$metadata?.httpStatusCode}`
			);
		}

		// Validate response contains required fields
		if (!uploadCompleteOutput?.Location || !uploadCompleteOutput?.ETag) {
			throw new Error(
				"S3 upload response missing Location or ETag - upload may not have completed"
			);
		}

		// CRITICAL: Verify file actually exists in S3
		let verificationAttempts = 0;
		const MAX_VERIFICATION_ATTEMPTS = 3;
		let s3FileSize: number | undefined;

		while (verificationAttempts < MAX_VERIFICATION_ATTEMPTS) {
			try {
				s3FileSize = yield call(
					uploader.fetchFileSize,
					s3Config.bucket,
					fileKey
				);

				if (s3FileSize && s3FileSize > 0) {
					console.log(
						`File verification SUCCESS: ${fileKey} Size: ${s3FileSize} bytes`
					);
					break; // Success - file exists
				} else {
					console.warn(
						`File verification returned size: ${s3FileSize} - retrying...`
					);
					verificationAttempts++;
					if (verificationAttempts < MAX_VERIFICATION_ATTEMPTS) {
						// Wait before retry (S3 eventual consistency)
						yield new Promise((resolve) =>
							setTimeout(resolve, 1000 * verificationAttempts)
						);
					}
				}
			} catch (verifyError: any) {
				verificationAttempts++;
				const verifyErrorDetails = {
					fileId: fileMeta.id,
					attempt: verificationAttempts,
					bucket: s3Config.bucket,
					key: fileKey,
					error: verifyError?.message,
					code: verifyError?.code,
				};
				console.warn(
					`Verification attempt ${verificationAttempts} failed:`,
					JSON.stringify(verifyErrorDetails, null, 2)
				);

				if (verificationAttempts >= MAX_VERIFICATION_ATTEMPTS) {
					throw new Error(
						`File verification failed after ${MAX_VERIFICATION_ATTEMPTS} attempts: ${verifyError?.message}`
					);
				}
				// Wait before retry
				yield new Promise((resolve) =>
					setTimeout(resolve, 1000 * verificationAttempts)
				);
			}
		}

		if (!s3FileSize || s3FileSize === 0) {
			throw new Error(
				`File verification failed: File size is ${s3FileSize} bytes`
			);
		}

		// Only mark as complete after verification passes
		yield put(
			orderItemUpdateRequested({
				file: {
					...fileMeta,
					status: FileUploadStatus.UploadComplete,
					uploadedUrl: uploadCompleteOutput.Location,
				},
			})
		);
	} catch (error: any) {
		const errorDetails = {
			fileId: fileMeta.id,
			fileName: fileMeta.name,
			fileSize: fileMeta.size,
			stage: "handleSingleUpload",
			errorMessage: error?.message,
			errorCode: error?.code,
			statusCode: error?.$metadata?.httpStatusCode || error?.statusCode,
			timestamp: new Date().toISOString(),
		};

		console.error("Upload failed:", JSON.stringify(errorDetails, null, 2));
		yield call(logClientError, {
			level: "error",
			event: "single_file_upload_failed",
			message: `Upload failed for ${fileMeta.name}`,
			error,
			context: errorDetails,
		});

		// commented bcz the file will not marked as failed it will retry first, and marked failed in safeuploadtast()
		// yield put(
		// 	orderItemUpdateRequested({
		// 		file: { ...fileMeta, status: FileUploadStatus.Failed },
		// 	})
		// );

		// yield put(
		// 	documentUploadError(
		// 		`Upload failed for ${fileMeta.name}: ${error?.message}`
		// 	)
		// );

		throw error;
	}
}

/* ============================================================
   STEP 4: Main Upload Pipeline
   ============================================================ */
const BATCH_SIZE = 10;
function* uploadFilesSaga(): SagaIterator {
	try {
		const state = yield select((s) => s.upload);

		const encrypted = state.s3BucketInfo.encryptedBucketInfo;

		if (!encrypted) {
			throw new Error("No encrypted S3 bucket info available");
		}

		let decrypted: string;
		let parsed: any;

		try {
			decrypted = decryptAES(encrypted, DECRYPTION_KEY);
			parsed = JSON.parse(decrypted);
		} catch (decrypError: any) {
			throw new Error(
				`Failed to decrypt/parse S3 config: ${decrypError?.message}`
			);
		}

		const s3Config: IS3Config = {
			bucket: parsed?.BucketName,
			region: parsed?.AWSRegionEndpoint,
			cognitoRegion: "us-east-1",
			cognitoIdentityPoolId: parsed?.CognitoIdentityPoolID,
			folderName: parsed?.NeutralBucketRootFolderName,
		};

		// Validate S3 config before starting uploads
		if (
			!s3Config.bucket ||
			!s3Config.region ||
			!s3Config.cognitoIdentityPoolId
		) {
			throw new Error(
				`Invalid S3 configuration: bucket=${s3Config.bucket}, region=${s3Config.region}, poolId=${s3Config.cognitoIdentityPoolId}`
			);
		}

		yield put(setS3FolderName(parsed?.NeutralBucketRootFolderName));

		const files: DriveFileUpload[] = state.files;
		console.log(
			` Starting upload of ${files.length} files in batches of ${BATCH_SIZE}`
		);

		// Create channel
		const workChannel: Channel<DriveFileUpload> = channel(
			buffers.fixed(files.length)
		);

		// Push all files into channel
		for (const file of files) {
			workChannel.put(file);
		}
		workChannel.close();

		// Create workers (parallel uploads)
		const totalWorkers = Math.min(BATCH_SIZE, files.length);
		const workers: Task[] = [];

		for (let i = 0; i < totalWorkers; i++) {
			const worker: Task = yield fork(workerTask, workChannel, s3Config);
			workers.push(worker);
		}

		// Wait for all workers to finish
		yield join(workers);

		console.log(" All upload batches completed");
	} catch (error: any) {
		const pipelineErrorContext = {
			error: error?.message,
			scope: "uploadFilesSaga",
			timestamp: new Date().toISOString(),
		};
		console.error(
			" Upload pipeline error:",
			JSON.stringify(pipelineErrorContext, null, 2)
		);
		yield call(logClientError, {
			level: "error",
			event: "upload_pipeline_failed",
			message: "Upload pipeline failed",
			error,
			context: pipelineErrorContext,
		});
		yield put(documentUploadError(`Upload pipeline failed: ${error?.message}`));
	}
}

function* workerTask(
	workChannel: Channel<DriveFileUpload>,
	s3Config: IS3Config
): SagaIterator {
	while (true) {
		const file: DriveFileUpload | END = yield take(workChannel);

		if (file === END) break;

		yield call(safeUploadTask as any, file, s3Config);
	}
}

function* safeUploadTask(file: DriveFileUpload, s3Config: IS3Config) {
	const MAX_RETRIES = 5;
	let attempt = 0;

	while (attempt < MAX_RETRIES) {
		try {
			const realFile: File | null = fileStore.getFileById(file.id);
			if (!realFile || realFile.size === 0) {
				throw new Error(
					"Local file is no longer readable or was deleted by the user."
				);
			}

			console.log(
				`Starting upload: ${file.name} (Attempt ${attempt + 1}/${MAX_RETRIES})`
			);

			yield call(handleSingleUpload, file, s3Config);

			console.log(`Upload successful: ${file.name}`);
			return { status: "fulfilled", fileId: file.id };
		} catch (error: any) {
			// 1. FIRST: Check if the user is completely offline.
			// If they are, we pause indefinitely WITHOUT burning one of our 5 retries.
			if (!navigator.onLine) {
				console.log(
					` Internet disconnected! Pausing ${file.name} until connection is restored...`
				);

				toast.error(
					"Your network connection is offline. Uploads will wait and resume automatically once you're back online.",
					{
						id: "network-offline-toast",
						duration: Infinity,
					}
				);

				// This completely freezes the loop until the user reconnects (even if it takes 5 hours)
				yield call(waitForInternet);

				toast.success("Network connection restored. Resuming uploads...", {
					id: "network-offline-toast",
					duration: 10000,
				});
				console.log(` Internet restored! Resuming ${file.name}...`);
				continue;
			}
			attempt++;
			console.warn(
				` Upload failed for ${file.name} (Attempt ${attempt}/${MAX_RETRIES})`,
				error?.message
			);

			if (attempt >= MAX_RETRIES) {
				console.error(` Fatal: Exhausted all retries for ${file.name}`);

				yield call(logClientError, {
					level: "error",
					event: "safe_upload_task_failed",
					message: `safeUploadTask failed after ${MAX_RETRIES} attempts for ${file.name}`,
					error,
					context: { fileId: file.id, fileName: file.name },
				});

				yield put(
					orderItemUpdateRequested({
						file: { ...file, status: FileUploadStatus.Failed },
					})
				);

				yield put(
					documentUploadError(
						`Upload failed for ${file.name}: after ${MAX_RETRIES} attempts${error?.message}`
					)
				);

				return { status: "rejected", fileId: file.id, error };
			}

			const delayMs = attempt * 2000;
			toast.error(`Upload interrupted for "${file.name}"`, {
				id: `retry-toast-${file.id}`,
				duration: 5000,
			});
			console.log(
				` Network drop detected. Waiting ${delayMs}ms before retrying...`
			);
			yield delay(delayMs);
		}
	}
}

/* ============================================================
   ROOT SAGA
   ============================================================ */
export default function* uploadSaga() {
	yield all([
		takeLatest(s3BucketInfoRequested.type, fetchS3BucketDetails),
		takeLatest(initiateUpload.type, uploadFilesSaga),
	]);
}
